import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Typography,
  Breadcrumb,
  Tabs,
  Form,
  Input,
  Button,
  DatePicker,
  InputNumber,
  message,
  Tag,
  Space,
  Drawer,
  Empty,
  Spin,
  Popconfirm,
  Radio,
  Divider,
  List,
  Pagination,
  Alert,
  Table,
  Avatar
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  HomeOutlined,
  CalendarOutlined,
  PlusOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  RightOutlined,
  DownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiPublicBase } from '../../utils/apiPublicBase';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import {
  createLabActivity,
  getLabActivityList,
  getLabActivityById,
  registerLabActivity,
  getLabActivityRegistrations,
  reviewLabActivityRegistration,
  cancelLabActivity,
  getLabTeamParticipation,
  downloadLabTeamParticipationExport,
  type LabActivityItem,
  type LabActivityRegistrationItem,
  type LabTeamParticipationRow
} from '../../services/api';
import { getDemoLabActivities, getDemoLabActivityById, getDemoLabTeamParticipation } from '../../demo/demoBrowseMocks';
import './labActivitiesPage.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

type ListTab = 'all' | 'mine' | 'registered';

function canExportLabParticipation(
  role: string | null | undefined,
  teamRole: string | null | undefined
): boolean {
  if (role === '超级管理员') return false;
  if (role === '管理员') return true;
  if (teamRole === 'owner' || teamRole === 'admin') return true;
  return false;
}

function resolveAvatarUrl(avatar?: string | null): string | undefined {
  if (!avatar) return undefined;
  if (avatar.startsWith('http')) return avatar;
  const base = getApiPublicBase().replace(/\/$/, '');
  if (avatar.startsWith('/')) return `${base || ''}${avatar}`;
  return avatar;
}

const CreateActivity: React.FC = () => {
  const { user, isDemoBrowseOnly } = useAuth();
  const hideTeamParticipationTab = user?.role === '超级管理员';
  const currentUserId = user?._id || user?.id || null;
  const [searchParams] = useSearchParams();
  const activityIdFromUrl = searchParams.get('activityId');
  const [activeTab, setActiveTab] = useState<string>('create');
  const [listFilter, setListFilter] = useState<ListTab>('all');
  const [list, setList] = useState<LabActivityItem[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [listLoading, setListLoading] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [detailActivity, setDetailActivity] = useState<LabActivityItem | null>(null);
  const [detailRegistrations, setDetailRegistrations] = useState<LabActivityRegistrationItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelLoadingId, setCancelLoadingId] = useState<string | null>(null);
  const [participationRows, setParticipationRows] = useState<LabTeamParticipationRow[]>([]);
  const [participationLoading, setParticipationLoading] = useState(false);
  const [participationError, setParticipationError] = useState<string | null>(null);
  const [participationGeneratedAt, setParticipationGeneratedAt] = useState<string | null>(null);
  const [exportDateRange, setExportDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const loadList = async (tab: ListTab, page = 1) => {
    setListLoading(true);
    try {
      if (isDemoBrowseOnly) {
        let rows = [...getDemoLabActivities()];
        if (tab === 'mine' && currentUserId) {
          const mine = rows.filter(
            (r) => typeof r.createdBy === 'object' && String(r.createdBy._id) === String(currentUserId)
          );
          rows = mine.length ? mine : rows;
        }
        if (tab === 'registered') {
          rows = rows.slice(1);
        }
        setList(rows);
        setPagination((p) => ({ ...p, current: page, total: rows.length }));
        return;
      }
      const res = await getLabActivityList({ tab, page, limit: pagination.pageSize });
      if (res.success && res.data) {
        setList(res.data.list);
        setPagination((p) => ({ ...p, current: res.data!.pagination.current, total: res.data!.pagination.total }));
      }
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (hideTeamParticipationTab && activeTab === 'team-participation') {
      setActiveTab('create');
    }
  }, [hideTeamParticipationTab, activeTab]);

  useEffect(() => {
    if (activeTab === 'list') void loadList(listFilter, pagination.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, listFilter, pagination.current, isDemoBrowseOnly]);

  useEffect(() => {
    if (activityIdFromUrl && activeTab !== 'list') {
      setActiveTab('list');
      setTimeout(() => openDetail(activityIdFromUrl), 100);
    }
  }, [activityIdFromUrl]);

  useEffect(() => {
    if (activityIdFromUrl && activeTab === 'list') {
      openDetail(activityIdFromUrl);
    }
  }, [activityIdFromUrl, activeTab]);

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailActivity(null);
    setDetailRegistrations([]);
    try {
      if (isDemoBrowseOnly && id.startsWith('demo-la-')) {
        const act = getDemoLabActivityById(id);
        if (act) setDetailActivity(act);
        setDetailRegistrations([]);
        return;
      }
      const [actRes, regRes] = await Promise.all([getLabActivityById(id), getLabActivityRegistrations(id)]);
      if (actRes.success && actRes.data) setDetailActivity(actRes.data);
      if (regRes.success && regRes.data) setDetailRegistrations(regRes.data);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadTeamParticipation = useCallback(async () => {
    setParticipationLoading(true);
    setParticipationError(null);
    try {
      if (isDemoBrowseOnly) {
        const d = getDemoLabTeamParticipation();
        setParticipationRows(d.rows);
        setParticipationGeneratedAt(d.generatedAt);
        return;
      }
      const res = await getLabTeamParticipation();
      if (res.success && res.data) {
        setParticipationRows(res.data.rows);
        setParticipationGeneratedAt(res.data.generatedAt);
      } else {
        setParticipationRows([]);
        setParticipationGeneratedAt(null);
        setParticipationError(typeof res.error === 'string' ? res.error : '加载失败');
      }
    } catch {
      setParticipationRows([]);
      setParticipationGeneratedAt(null);
      setParticipationError('加载失败');
    } finally {
      setParticipationLoading(false);
    }
  }, [isDemoBrowseOnly]);

  useEffect(() => {
    if (activeTab === 'team-participation') void loadTeamParticipation();
  }, [activeTab, loadTeamParticipation]);

  const participationColumns: ColumnsType<LabTeamParticipationRow> = useMemo(
    () => [
      {
        title: '成员',
        key: 'member',
        width: 200,
        ellipsis: true,
        render: (_: unknown, row) => (
          <div className="lab-act-stats-member-cell">
            <Avatar size={36} src={resolveAvatarUrl(row.avatar)} className="lab-act-stats-avatar">
              {(row.name && row.name[0]) || '?'}
            </Avatar>
            <span className="lab-act-stats-member-name">{row.name}</span>
          </div>
        )
      },
      {
        title: '学号',
        dataIndex: 'studentId',
        key: 'studentId',
        width: 140,
        className: 'lab-act-stats-col-mono',
        ellipsis: true
      },
      {
        title: '已通过',
        dataIndex: 'approvedCount',
        key: 'approvedCount',
        width: 100,
        align: 'center',
        render: (n: number) => (
          <span className={n > 0 ? 'lab-act-stat-num lab-act-stat-num--ok' : 'lab-act-stat-num lab-act-stat-num--muted'}>
            {n}
          </span>
        )
      },
      {
        title: '待审核',
        dataIndex: 'pendingCount',
        key: 'pendingCount',
        width: 100,
        align: 'center',
        render: (n: number) => (
          <span
            className={n > 0 ? 'lab-act-stat-num lab-act-stat-num--warn' : 'lab-act-stat-num lab-act-stat-num--muted'}
          >
            {n}
          </span>
        )
      },
      {
        title: '未通过',
        dataIndex: 'rejectedCount',
        key: 'rejectedCount',
        width: 100,
        align: 'center',
        render: (n: number) => (
          <span
            className={n > 0 ? 'lab-act-stat-num lab-act-stat-num--err' : 'lab-act-stat-num lab-act-stat-num--muted'}
          >
            {n}
          </span>
        )
      },
      {
        title: '最近参与',
        key: 'lastParticipatedAt',
        width: 130,
        render: (_: unknown, row) =>
          row.lastParticipatedAt ? (
            <span className="lab-act-stats-date">{dayjs(row.lastParticipatedAt).format('YYYY-MM-DD')}</span>
          ) : (
            <span className="lab-act-stats-never">从未</span>
          )
      }
    ],
    []
  );

  const onFinish = async (values: {
    title: string;
    description?: string;
    location?: string;
    startTime: dayjs.Dayjs;
    endTime?: dayjs.Dayjs;
    maxParticipants?: number;
  }) => {
    setSubmitting(true);
    try {
      const res = await createLabActivity({
        title: values.title.trim(),
        description: values.description?.trim(),
        location: values.location?.trim(),
        startTime: values.startTime.toISOString(),
        endTime: values.endTime ? values.endTime.toISOString() : undefined,
        maxParticipants: values.maxParticipants
      });
      if (res.success) {
        message.success('活动已发布，已通知本团队除「教师」类型外的成员');
        form.resetFields();
        setActiveTab('list');
        setListFilter('mine');
        loadList('mine', 1);
      } else {
        message.error(res.error || '发布失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (activityId: string) => {
    const res = await registerLabActivity(activityId);
    if (res.success) {
      const autoApproved = res.data?.status === 'approved';
      message.success(
        autoApproved ? '报名成功，已自动通过并计入公共事务' : '报名成功，等待发起人审核'
      );
      openDetail(activityId);
      loadList(listFilter, pagination.current);
    } else {
      message.error(res.error || '报名失败');
    }
  };

  const handleReview = async (registrationId: string, action: 'approve' | 'reject', rejectReason?: string) => {
    const res = await reviewLabActivityRegistration(registrationId, action, rejectReason);
      if (res.success) {
        message.success(action === 'approve' ? '已通过，已计入公共事务' : '已拒绝');
        if (detailActivity) {
          openDetail(detailActivity._id);
          loadList(listFilter, pagination.current);
          if (activeTab === 'team-participation') void loadTeamParticipation();
        }
      } else {
      message.error(res.error || '操作失败');
    }
  };

  const handleCancelActivity = async (activityId: string) => {
    setCancelLoadingId(activityId);
    try {
      const res = await cancelLabActivity(activityId);
      if (res.success) {
        message.success('活动已取消');
        loadList(listFilter, pagination.current);
        if (activeTab === 'team-participation') void loadTeamParticipation();
        if (detailOpen && detailActivity && detailActivity._id === activityId && res.data) {
          setDetailActivity(res.data);
          const regRes = await getLabActivityRegistrations(activityId);
          if (regRes.success && regRes.data) setDetailRegistrations(regRes.data);
        }
      } else {
        message.error(typeof res.error === 'string' ? res.error : '取消失败');
      }
    } finally {
      setCancelLoadingId(null);
    }
  };

  const createdById =
    detailActivity?.createdBy && typeof detailActivity.createdBy === 'object'
      ? (detailActivity.createdBy as { _id: string })._id
      : (detailActivity?.createdBy as string);
  const isDetailCreator = !!currentUserId && createdById === currentUserId;

  const pendingList = detailRegistrations.filter((r) => r.status === 'pending');
  const approvedList = detailRegistrations.filter((r) => r.status === 'approved');
  const detailActiveCount =
    detailActivity?.registrationActiveCount ??
    (detailActivity?.registrationPendingCount || 0) + (detailActivity?.registrationApprovedCount || 0);
  const detailIsFull =
    detailActivity?.isRegistrationFull ??
    (!!detailActivity?.maxParticipants && detailActiveCount >= detailActivity.maxParticipants);

  const breadcrumbLast =
    activeTab === 'team-participation'
      ? '团队统计'
      : activeTab === 'list'
        ? `活动列表 · ${listFilter === 'all' ? '全部' : listFilter === 'mine' ? '我发起的' : '我报名的'}`
        : '创建活动';

  const handleExportParticipation = async () => {
    if (exportDateRange && (!exportDateRange[0] || !exportDateRange[1])) {
      message.warning('请选择完整的导出日期范围');
      return;
    }
    setExportLoading(true);
    try {
      await downloadLabTeamParticipationExport(
        exportDateRange
          ? {
              startDate: exportDateRange[0].format('YYYY-MM-DD'),
              endDate: exportDateRange[1].format('YYYY-MM-DD')
            }
          : undefined
      );
      message.success('导出成功');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="page-container full-height">
      <Breadcrumb
        className="lab-act-breadcrumb"
        items={[
          {
            title: (
              <Link to="/dashboard">
                <HomeOutlined />
                <span style={{ marginLeft: 6 }}>首页</span>
              </Link>
            )
          },
          { title: '实验室活动' },
          { title: breadcrumbLast }
        ]}
      />

      {activeTab === 'team-participation' ? (
        <header className="lab-act-hero-stats">
          <h1 className="lab-act-hero-stats__title">团队参与统计</h1>
          <p className="lab-act-hero-stats__subtitle">
            掌握成员在实验室公共事务与集体活动中的报名与审核结果；数据按当前团队成员及本团队发起的活动汇总。
          </p>
        </header>
      ) : (
        <div className="lab-act-page-header">
          <div className="lab-act-title-row">
            <div className="lab-act-title-icon" aria-hidden>
              <LimsNavIcon name="event" sizePx={26} />
            </div>
            <div>
              <h1 className="page-title">发起活动</h1>
              <p className="page-subtitle">发布公共事务活动，成员可报名；审核通过后计入公共事务</p>
            </div>
          </div>
        </div>
      )}

      <Card className="lab-act-shell-card" bordered={false}>
        <Tabs
          className="lab-act-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'create',
              label: '创建活动',
              children: (
                <div className="lab-act-form-panel">
                  <Paragraph type="secondary" style={{ marginBottom: 20 }}>
                    填写时间与地点后发布，本团队成员将收到站内与邮件通知（成员类型为「教师」的账号除外）。
                  </Paragraph>
                  <Form form={form} layout="vertical" onFinish={onFinish} requiredMark="optional">
                    <Form.Item name="title" label="活动标题" rules={[{ required: true, message: '请输入活动标题' }]}>
                      <Input placeholder="例如：实验室大扫除、公共空间整理" size="large" allowClear />
                    </Form.Item>
                    <Form.Item name="description" label="活动说明">
                      <TextArea rows={4} placeholder="补充任务要求、集合方式等（选填）" showCount maxLength={2000} />
                    </Form.Item>
                    <Form.Item name="location" label="地点">
                      <Input placeholder="选填，如：会议室 A、实验楼主厅" size="large" allowClear />
                    </Form.Item>
                    <Form.Item name="startTime" label="开始时间" rules={[{ required: true, message: '请选择开始时间' }]}>
                      <DatePicker showTime style={{ width: '100%' }} size="large" placeholder="选择日期与时间" />
                    </Form.Item>
                    <Form.Item name="endTime" label="结束时间">
                      <DatePicker showTime style={{ width: '100%' }} size="large" placeholder="选填" />
                    </Form.Item>
                    <Form.Item
                      name="maxParticipants"
                      label="人数上限"
                      extra="留空表示不限制；统计含待审核与已通过报名，满员后不再接受新报名"
                    >
                      <InputNumber
                        min={1}
                        max={9999}
                        precision={0}
                        placeholder="例如：20"
                        style={{ width: '100%' }}
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item className="lab-act-primary-actions">
                      <Button type="primary" htmlType="submit" loading={submitting} icon={<PlusOutlined />} size="large">
                        发布活动
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              )
            },
            {
              key: 'list',
              label: '活动列表',
              children: (
                <div className="lab-act-tab-panel lab-act-tab-panel--list">
                  <div className="lab-act-list-toolbar">
                    <Radio.Group
                      className="lab-act-filter-radio"
                      value={listFilter}
                      optionType="button"
                      buttonStyle="solid"
                      onChange={(e) => {
                        const v = e.target.value as ListTab;
                        setListFilter(v);
                        loadList(v, 1);
                      }}
                    >
                      <Radio.Button value="all">全部</Radio.Button>
                      <Radio.Button value="mine">我发起的</Radio.Button>
                      <Radio.Button value="registered">我报名的</Radio.Button>
                    </Radio.Group>
                  </div>
                  <Spin spinning={listLoading}>
                    {list.length === 0 && !listLoading ? (
                      <Empty className="lab-act-empty-inline" description="暂无活动" />
                    ) : (
                      <div className="lab-act-activity-list">
                        {list.map((item) => {
                          const creator = typeof item.createdBy === 'object' ? item.createdBy : null;
                          const creatorId =
                            creator?._id ||
                            (typeof item.createdBy === 'string' ? item.createdBy : '');
                          const isListCreator = !!currentUserId && creatorId === currentUserId;
                          const isCancelled = item.status === 'cancelled';
                          const activeCount =
                            item.registrationActiveCount ??
                            (item.registrationPendingCount || 0) + (item.registrationApprovedCount || 0);
                          const isFull =
                            item.isRegistrationFull ??
                            (!!item.maxParticipants && activeCount >= item.maxParticipants);
                          return (
                            <Card
                              key={item._id}
                              className={`lab-act-activity-card${isCancelled ? ' lab-act-activity-card--cancelled' : ''}`}
                              bordered={false}
                              onClick={() => openDetail(item._id)}
                            >
                              <div className="lab-act-activity-card-meta">
                                <div className="lab-act-activity-card-icon">
                                  <CalendarOutlined />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="lab-act-activity-card-title-row">
                                    <span className="lab-act-activity-card-title">{item.title}</span>
                                    {isCancelled && (
                                      <Tag color="default" style={{ marginLeft: 8, flexShrink: 0 }}>
                                        已取消
                                      </Tag>
                                    )}
                                    {!isCancelled && isFull && (
                                      <Tag color="orange" style={{ marginLeft: 8, flexShrink: 0 }}>
                                        已满员
                                      </Tag>
                                    )}
                                  </div>
                                  <Space wrap size={[6, 6]}>
                                    {item.startTime && (
                                      <Text type="secondary">{dayjs(item.startTime).format('YYYY-MM-DD HH:mm')}</Text>
                                    )}
                                    {item.location && <Text type="secondary">{item.location}</Text>}
                                    {creator && (
                                      <Text type="secondary">
                                        发起人：{creator.name}
                                      </Text>
                                    )}
                                  </Space>
                                  <div className="lab-act-list-pills" role="list" aria-label="报名统计">
                                    <span className="lab-act-list-pill lab-act-list-pill--pending">
                                      待审 <strong>{item.registrationPendingCount || 0}</strong>
                                    </span>
                                    <span className="lab-act-list-pill lab-act-list-pill--approved">
                                      通过 <strong>{item.registrationApprovedCount || 0}</strong>
                                    </span>
                                    {item.maxParticipants != null && (
                                      <span className="lab-act-list-pill">
                                        名额 <strong>{activeCount}/{item.maxParticipants}</strong>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div
                                className="lab-act-activity-card-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Space wrap>
                                  <Button
                                    type="default"
                                    size="small"
                                    className="lab-act-list-detail-btn"
                                    onClick={() => openDetail(item._id)}
                                  >
                                    查看详情
                                  </Button>
                                  {isListCreator && !isCancelled && (
                                    <Popconfirm
                                      title="确定取消该活动？"
                                      description="取消后无法报名与审核；已报名记录仍可在详情中查看。"
                                      okText="确定取消"
                                      cancelText="返回"
                                      okButtonProps={{ danger: true }}
                                      onConfirm={() => void handleCancelActivity(item._id)}
                                    >
                                      <Button
                                        danger
                                        size="small"
                                        loading={cancelLoadingId === item._id}
                                      >
                                        取消活动
                                      </Button>
                                    </Popconfirm>
                                  )}
                                </Space>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </Spin>
                  {list.length > 0 && pagination.total > pagination.pageSize && (
                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger={false}
                        onChange={(page) => loadList(listFilter, page)}
                      />
                    </div>
                  )}
                </div>
              )
            },
            ...(hideTeamParticipationTab
              ? []
              : [
                  {
                    key: 'team-participation',
                    label: '团队统计',
                    children: (
                      <div className="lab-act-tab-panel lab-act-tab-panel--stats">
                        <div className="lab-act-stats-protocol" role="note">
                          <InfoCircleOutlined className="lab-act-stats-protocol__icon" aria-hidden />
                          <div>
                            <div className="lab-act-stats-protocol__title">统计口径说明</div>
                            <p className="lab-act-stats-protocol__body">
                              仅统计由<strong>当前团队在职成员</strong>发起的实验室活动中的报名记录。「已通过」为发起人审核通过并计入公共事务的次数；已离职成员或外部账号的历史数据不在此表汇总。
                              <strong>已加入团队的成员</strong>可查看本表；<strong>平台超级管理员</strong>不提供本页查看与导出（请使用团队内账号）。
                              导出 Excel 仅限租户「管理员」及团队负责人/管理员；可按活动开始日期筛选导出范围，不选则导出全部历史数据。
                            </p>
                          </div>
                        </div>

                  <div className="lab-act-stats-toolbar">
                    <Space wrap size="middle" align="end">
                      {canExportLabParticipation(user?.role, user?.teamRole) && (
                        <>
                          <div className="lab-act-stats-export-range">
                            <span className="lab-act-stats-export-range__label">导出日期范围</span>
                            <RangePicker
                              value={exportDateRange}
                              onChange={(dates) =>
                                setExportDateRange(
                                  dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
                                )
                              }
                              allowClear
                              format="YYYY-MM-DD"
                              placeholder={['开始日期', '结束日期']}
                              className="lab-act-stats-export-range__picker"
                            />
                          </div>
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            className="lab-act-stats-btn-export"
                            onClick={() => void handleExportParticipation()}
                            loading={exportLoading}
                          >
                            导出 Excel
                          </Button>
                        </>
                      )}
                      <Button
                        icon={<ReloadOutlined />}
                        className="lab-act-stats-btn-ghost"
                        onClick={() => void loadTeamParticipation()}
                        loading={participationLoading}
                      >
                        刷新
                      </Button>
                    </Space>
                    {participationGeneratedAt && (
                      <div className="lab-act-stats-time-pill">
                        <ClockCircleOutlined aria-hidden />
                        <span>
                          数据截至：{dayjs(participationGeneratedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </span>
                      </div>
                    )}
                  </div>

                  {participationError && (
                    <Alert type="error" showIcon message={participationError} className="lab-act-stats-alert" />
                  )}

                  <Spin spinning={participationLoading}>
                    <div className="lab-act-stats-table-wrap">
                      <Table<LabTeamParticipationRow>
                        rowKey="userId"
                        size="middle"
                        className="lab-act-stats-table"
                        columns={participationColumns}
                        dataSource={participationRows}
                        pagination={{
                          pageSize: 20,
                          showSizeChanger: true,
                          pageSizeOptions: ['10', '20', '50', '100'],
                          showTotal: (total, range) =>
                            `第 ${range[0]}-${range[1]} 条，共 ${total} 位成员`
                        }}
                        scroll={{ x: 860 }}
                        locale={{
                          emptyText: (
                            <div className="lab-act-stats-empty">
                              <div className="lab-act-stats-empty__icon" aria-hidden>
                                <BarChartOutlined />
                              </div>
                              <Title level={4} className="lab-act-stats-empty__title">
                                暂无参与记录
                              </Title>
                              <Paragraph type="secondary" className="lab-act-stats-empty__desc">
                                当前团队尚无符合条件的活动报名数据。发布活动并审核通过后，统计将自动更新。
                              </Paragraph>
                            </div>
                          )
                        }}
                        expandable={{
                          expandRowByClick: true,
                          expandIcon: ({ expanded, onExpand, record }) => (
                            <button
                              type="button"
                              className="lab-act-stats-expand-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                onExpand(record, e);
                              }}
                              aria-expanded={expanded}
                              aria-label={expanded ? '收起活动详情' : '展开查看近期活动'}
                            >
                              {expanded ? <DownOutlined /> : <RightOutlined />}
                            </button>
                          ),
                          expandedRowRender: (row) => (
                            <div className="lab-act-stats-expand">
                              <div className="lab-act-stats-expand__inner">
                                <div className="lab-act-stats-expand__heading">报名概况</div>
                                <p className="lab-act-stats-expand__summary">
                                  已通过 <strong>{row.approvedCount}</strong> · 待审核{' '}
                                  <strong>{row.pendingCount}</strong> · 未通过 <strong>{row.rejectedCount}</strong>
                                </p>
                                <div className="lab-act-stats-expand__heading lab-act-stats-expand__heading--sub">
                                  近期已通过活动
                                </div>
                                {row.approvedActivities.length === 0 ? (
                                  <Paragraph type="secondary" className="lab-act-stats-expand__empty">
                                    暂无已通过记录。若有待审核或已拒绝的报名，可在「活动列表 → 我报名的」中进入活动详情查看。
                                  </Paragraph>
                                ) : (
                                  <div className="lab-act-stats-expand__list">
                                    {row.approvedActivities.map((a) => (
                                      <div key={a.activityId} className="lab-act-stats-expand__card">
                                        <div className="lab-act-stats-expand__card-main">
                                          <div className="lab-act-stats-expand__card-title">{a.title}</div>
                                          <div className="lab-act-stats-expand__card-meta">
                                            {dayjs(a.startTime).format('YYYY-MM-DD HH:mm')}
                                          </div>
                                        </div>
                                        {a.activityStatus === 'cancelled' ? (
                                          <span className="lab-act-stats-pill lab-act-stats-pill--cancelled">已取消</span>
                                        ) : (
                                          <span className="lab-act-stats-pill lab-act-stats-pill--approved">已通过</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                          rowExpandable: () => true
                        }}
                      />
                    </div>
                  </Spin>
                      </div>
                    )
                  }
                ])
          ]}
        />
      </Card>

      <Drawer
        title={
          detailActivity
            ? `${detailActivity.title}${detailActivity.status === 'cancelled' ? '（已取消）' : ''}`
            : '活动详情'
        }
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={520}
        rootClassName="lab-act-drawer"
        destroyOnHidden
      >
        <Spin spinning={detailLoading}>
          {detailActivity && (
            <>
              {detailActivity.status === 'cancelled' && (
                <Alert
                  type="warning"
                  showIcon
                  message="该活动已由发起人取消"
                  description="不再接受报名与审核；历史报名记录如下。"
                  style={{ marginBottom: 16 }}
                />
              )}
              {isDetailCreator && detailActivity.status === 'published' && (
                <div style={{ marginBottom: 16 }}>
                  <Popconfirm
                    title="确定取消该活动？"
                    description="取消后成员无法报名，也无法继续审核待处理报名。"
                    okText="确定取消"
                    cancelText="返回"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => void handleCancelActivity(detailActivity._id)}
                  >
                    <Button danger loading={cancelLoadingId === detailActivity._id}>
                      取消活动
                    </Button>
                  </Popconfirm>
                </div>
              )}
              <div className="lab-act-drawer-section">
                <Paragraph className="lab-act-drawer-kv">
                  <strong>说明</strong> {detailActivity.description || '无'}
                </Paragraph>
                <Paragraph className="lab-act-drawer-kv">
                  <strong>地点</strong> {detailActivity.location || '未填'}
                </Paragraph>
                <Paragraph className="lab-act-drawer-kv" style={{ marginBottom: 0 }}>
                  <strong>时间</strong> {dayjs(detailActivity.startTime).format('YYYY-MM-DD HH:mm')}
                  {detailActivity.endTime ? ` 至 ${dayjs(detailActivity.endTime).format('YYYY-MM-DD HH:mm')}` : ''}
                </Paragraph>
                <Paragraph className="lab-act-drawer-kv" style={{ marginTop: 10 }}>
                  <strong>发起人</strong>{' '}
                  {typeof detailActivity.createdBy === 'object'
                    ? (detailActivity.createdBy as { name: string }).name
                    : '-'}
                </Paragraph>
                {detailActivity.maxParticipants != null && (
                  <Paragraph className="lab-act-drawer-kv" style={{ marginTop: 10, marginBottom: 0 }}>
                    <strong>报名名额</strong>{' '}
                    {detailActiveCount}/{detailActivity.maxParticipants} 人
                    {detailIsFull ? '（已满员）' : ''}
                  </Paragraph>
                )}
                {!detailActivity.myRegistration && detailActivity.status === 'published' && detailIsFull && (
                  <Alert
                    type="info"
                    showIcon
                    message="报名人数已达上限"
                    description={`该活动限 ${detailActivity.maxParticipants} 人报名，当前名额已满。请关注其他活动或联系发起人了解是否有空余名额。`}
                    style={{ marginTop: 12 }}
                  />
                )}
                {!detailActivity.myRegistration && detailActivity.status === 'published' && !detailIsFull && (
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    onClick={() => handleRegister(detailActivity._id)}
                    style={{ marginTop: 12, background: 'var(--lims-primary)', borderColor: 'var(--lims-primary)' }}
                    block
                  >
                    报名参加活动
                  </Button>
                )}
                {detailActivity.myRegistration && (
                  <Tag
                    style={{ marginTop: 12 }}
                    color={
                      detailActivity.myRegistration.status === 'approved'
                        ? 'success'
                        : detailActivity.myRegistration.status === 'rejected'
                          ? 'error'
                          : 'warning'
                    }
                  >
                    {detailActivity.myRegistration.status === 'approved'
                      ? '您的报名已通过'
                      : detailActivity.myRegistration.status === 'rejected'
                        ? '您的报名未通过'
                        : '报名审核中'}
                  </Tag>
                )}
              </div>

              <Divider style={{ margin: '8px 0 16px' }} />

              <div className="lab-act-drawer-section">
                <Title level={5} style={{ marginTop: 0 }}>
                  报名列表
                </Title>
                {pendingList.length > 0 && isDetailCreator && detailActivity.status === 'published' && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">待审核</Text>
                    {pendingList.map((reg) => (
                      <div key={reg._id} className="lab-act-reg-row">
                        <span>
                          {typeof reg.user === 'object' && reg.user
                            ? (reg.user as { name?: string; _id?: string }).name ||
                              (reg.user as { _id?: string })._id
                            : '-'}
                        </span>
                        <Space wrap>
                          <Popconfirm
                            title="确定通过？"
                            description="通过后将计入该成员公共事务一次。"
                            onConfirm={() => handleReview(reg._id, 'approve')}
                          >
                            <Button type="primary" size="small" icon={<CheckOutlined />}>
                              通过
                            </Button>
                          </Popconfirm>
                          <Popconfirm title="确定拒绝该报名？" onConfirm={() => handleReview(reg._id, 'reject')}>
                            <Button size="small" danger icon={<CloseOutlined />}>
                              拒绝
                            </Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    ))}
                  </div>
                )}
                {approvedList.length > 0 && (
                  <div>
                    <Text type="secondary">已通过（已计入公共事务）</Text>
                    <List
                      size="small"
                      dataSource={approvedList}
                      renderItem={(reg) => (
                        <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                          {typeof reg.user === 'object' && reg.user ? reg.user.name || reg.user._id : '-'}
                        </List.Item>
                      )}
                    />
                  </div>
                )}
                {detailRegistrations.length === 0 && !detailLoading && <Empty description="暂无报名" />}
              </div>
            </>
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default CreateActivity;
